import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CalendarDays, Route, ShieldCheck, Receipt, MessageCircle, Sparkles, Check, ArrowRight, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SALES_WHATSAPP = ""; // Will fetch from master settings

const features = [
  { icon: CalendarDays, title: "Agenda 24/7", desc: "Cliente marca sozinho pelo link. Você e sua equipe veem tudo no painel em tempo real." },
  { icon: Route, title: "Cobrança por km", desc: "Serviço com deslocamento? Sistema calcula automaticamente ida+volta pelo endereço." },
  { icon: Receipt, title: "Cobrança Asaas", desc: "Gere PIX, boleto ou cartão em um clique. Consolide vários serviços em uma única fatura." },
  { icon: ShieldCheck, title: "Permissões por função", desc: "Controle quem edita, exclui ou cobra. Cada função só vê o que precisa." },
  { icon: MessageCircle, title: "Lembrete WhatsApp", desc: "Envie confirmação de agendamento e cobrança direto pelo WhatsApp com um clique." },
  { icon: Sparkles, title: "Marca personalizada", desc: "Sua logo, cores, contatos e página pública com seu domínio (agenda/sua-marca)." },
];

const plans = [
  { name: "Starter", price: "97", desc: "Ideal para negócio começando", items: ["Agenda ilimitada", "Até 2 profissionais", "Cobrança Asaas", "Suporte por WhatsApp"] },
  { name: "Pro", price: "197", desc: "Mais popular", featured: true, items: ["Tudo do Starter", "Profissionais ilimitados", "Multi-usuários com permissões", "Relatórios avançados", "Marca personalizada"] },
  { name: "Franquia", price: "sob consulta", desc: "White-label completo", items: ["Tudo do Pro", "Domínio próprio", "Cadastro de sub-franquias", "Prioridade no suporte"] },
];

export default function SalesLanding() {
  const [whats, setWhats] = useState(SALES_WHATSAPP);
  const [brand, setBrand] = useState({});

  useEffect(() => {
    axios.get(`${API}/public/master/settings`).then(({ data }) => {
      setBrand(data);
      if (data.whatsapp) setWhats(data.whatsapp.replace(/\D/g, ""));
    }).catch(() => {});
  }, []);

  const contactMsg = encodeURIComponent("Olá! Quero uma agenda como essa para o meu negócio. Pode me passar mais detalhes?");
  const contactUrl = whats ? `https://wa.me/55${whats}?text=${contactMsg}` : `https://wa.me/?text=${contactMsg}`;

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b border-border bg-card/60 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="" className="h-8 w-8 rounded object-contain" />
            ) : (
              <div className="h-8 w-8 rounded bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-sm">A</div>
            )}
            <span className="font-display font-bold tracking-tight text-lg">Agenda<span className="text-accent">.</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="link-login" className="text-sm font-medium hover:text-primary transition-colors">Já sou cliente</Link>
            <a href={contactUrl} target="_blank" rel="noreferrer" data-testid="cta-contact-top">
              <Button size="sm" className="h-9"><MessageCircle className="h-4 w-4 mr-1" /> Falar no WhatsApp</Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-accent-foreground/80 bg-accent/15 border border-accent/30 rounded-full px-3 py-1.5 mb-6">
              <Sparkles className="h-3 w-3" /> Nova era da agenda online
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-tight">
              Sua agenda profissional, <span className="text-primary">sem esforço</span>.
            </h1>
            <p className="text-lg text-muted-foreground mt-6 leading-relaxed">
              Automatize agendamentos, cobre pelo PIX ou boleto e organize sua equipe em uma única plataforma feita para prestadores de serviço no Brasil.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={contactUrl} target="_blank" rel="noreferrer" data-testid="cta-hero">
                <Button size="lg" className="h-14 px-8 text-base"><MessageCircle className="h-5 w-5 mr-2" /> Quero uma agenda</Button>
              </a>
              <a href="#planos" data-testid="link-plans"><Button size="lg" variant="outline" className="h-14 px-8 text-base">Ver planos</Button></a>
            </div>
            <div className="flex items-center gap-6 mt-8 text-xs text-muted-foreground">
              <div className="flex items-center gap-1"><Check className="h-4 w-4 text-primary" /> Sem cartão de crédito</div>
              <div className="flex items-center gap-1"><Check className="h-4 w-4 text-primary" /> Pronto em 5 minutos</div>
            </div>
          </div>
          <div className="hidden md:block relative animate-fade-in-up">
            <div className="absolute -inset-4 bg-accent/20 blur-3xl rounded-3xl" />
            <img src="https://images.pexels.com/photos/13068377/pexels-photo-13068377.jpeg" alt="Painel" className="relative rounded-2xl shadow-2xl border border-border" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="max-w-2xl mb-12">
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Tudo em um só lugar</h2>
          <p className="text-muted-foreground mt-3">Recursos pensados para quem atende no dia a dia — sem complicação.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <div key={i} data-testid={`feat-${i}`} className="surface p-6 transition-transform duration-300 hover:-translate-y-1">
              <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4"><f.icon className="h-5 w-5" /></div>
              <h3 className="font-display font-semibold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="planos" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Planos que crescem com você</h2>
          <p className="text-muted-foreground mt-3">Cobrança mensal, sem multa de cancelamento.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p, i) => (
            <div key={i} data-testid={`plan-${p.name.toLowerCase()}`} className={`surface p-8 flex flex-col ${p.featured ? "border-primary border-2 relative" : ""}`}>
              {p.featured && <div className="absolute -top-3 left-8 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">Mais popular</div>}
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                {p.price === "sob consulta" ? (
                  <span className="text-2xl font-display font-bold">Sob consulta</span>
                ) : (
                  <>
                    <span className="text-sm text-muted-foreground">R$</span>
                    <span className="text-5xl font-display font-bold">{p.price}</span>
                    <span className="text-muted-foreground">/mês</span>
                  </>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-2">{p.desc}</p>
              <ul className="space-y-2 mt-6 mb-8 flex-1">
                {p.items.map((it) => <li key={it} className="text-sm flex items-start gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {it}</li>)}
              </ul>
              <a href={contactUrl} target="_blank" rel="noreferrer">
                <Button variant={p.featured ? "default" : "outline"} className="w-full h-12" data-testid={`btn-plan-${p.name.toLowerCase()}`}>Quero este plano <ArrowRight className="h-4 w-4 ml-2" /></Button>
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="surface p-10 md:p-16 text-center bg-primary text-primary-foreground border-primary">
          <Building2 className="h-12 w-12 mx-auto mb-4 opacity-80" />
          <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight">Pronto pra sua agenda?</h2>
          <p className="text-white/85 mt-3 max-w-lg mx-auto">Fale conosco no WhatsApp. Em minutos você tem seu link público e o painel prontos para receber clientes.</p>
          <a href={contactUrl} target="_blank" rel="noreferrer" data-testid="cta-final" className="inline-block mt-8">
            <Button size="lg" variant="secondary" className="h-14 px-10 text-base"><MessageCircle className="h-5 w-5 mr-2" /> Falar no WhatsApp</Button>
          </a>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} {brand.business_name || "Agenda"}. Todos os direitos reservados.
      </footer>
    </div>
  );
}
